import React from 'react'

import type {
  ResponseTemplate,
  CardTemplate as AppointmentCardTemplate,
  ListTemplate,
  QuickRepliesTemplate,
  FormTemplate,
  ResponseAction,
} from '../types/intent'

interface ResponseRendererProps {
  template: ResponseTemplate
  onAction?: (action: ResponseAction) => void
  onQuickReply?: (payload: string) => void
  onFormSubmit?: (data: Record<string, any>) => void
}

export const ResponseRenderer: React.FC<ResponseRendererProps> = ({
  template,
  onAction,
  onQuickReply,
  onFormSubmit,
}) => {
  const handleAction = (action: ResponseAction) => {
    if (onAction) {
      onAction(action)
    }
  }

  const handleQuickReply = (payload: string) => {
    if (onQuickReply) {
      onQuickReply(payload)
    }
  }

  const handleFormSubmit = (data: Record<string, any>) => {
    if (onFormSubmit) {
      onFormSubmit(data)
    }
  }

  // Check if quick_replies exists in data to render additionally
  const renderQuickRepliesFromData = (data: any) => {
    if (data?.quick_replies) {
      // Check if quick_replies has the correct structure
      const quickRepliesContent =
        data.quick_replies.content || data.quick_replies

      return (
        <div className="mt-4">
          <QuickRepliesTemplate
            content={quickRepliesContent}
            onQuickReply={handleQuickReply}
          />
        </div>
      )
    }
    return null
  }

  // Validate template structure
  if (!template || typeof template !== 'object') {
    console.error('Invalid template:', template)
    return (
      <TextTemplate content="Error: Invalid template" onAction={handleAction} />
    )
  }

  switch (template.type) {
    case 'text':
      return (
        <TextTemplate
          content={template.content?.text || ''}
          actions={template.actions}
          onAction={handleAction}
        />
      )

    case 'card':
      return (
        <AppointmentCardTemplate
          content={template.content}
          onAction={handleAction}
        />
      )

    case 'list':
      return <ListTemplate content={template.content} onAction={handleAction} />

    case 'carousel':
      return (
        <div>
          <CarouselTemplate
            content={template.content?.items || []}
            onAction={handleAction}
          />
          {/* Render quick replies if provided in template data */}
          {(template as any).data &&
            renderQuickRepliesFromData((template as any).data)}
        </div>
      )

    case 'quick_replies':
      return (
        <QuickRepliesTemplate
          content={template.content}
          onQuickReply={handleQuickReply}
        />
      )

    case 'form':
      return (
        <FormTemplate content={template.content} onSubmit={handleFormSubmit} />
      )

    case 'custom':
      return (
        <CustomTemplate content={template.content} onAction={handleAction} />
      )

    default:
      return (
        <TextTemplate
          content="Error: Unsupported template type"
          onAction={handleAction}
        />
      )
  }
}

// Text Template Component
const TextTemplate: React.FC<{
  content: string | React.ReactElement
  actions?: ResponseAction[]
  onAction?: (action: ResponseAction) => void
}> = ({ content, actions, onAction }) => {
  // Safely convert content to string
  // const textContent =
  //   typeof content === 'string'
  //     ? content
  //     : typeof content === 'object'
  //     ? JSON.stringify(content)
  //     : String(content || '')

  return (
    <div className="text-template">
      <div className="whitespace-pre-wrap text-sm">{content}</div>
      {actions && actions.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3">
          {actions.map((action, index) => (
            <ActionButton
              key={index}
              action={action}
              onClick={() => onAction?.(action)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

// Card Template Component
const AppointmentCardTemplate: React.FC<{
  content: AppointmentCardTemplate
  onAction?: (action: ResponseAction) => void
}> = ({ content, onAction }) => {
  // Check if this is a session card for special styling
  const isSessionCard = false
  const sessionStatus = content.data?.status

  // Styling based on session status
  const getStatusStyle = () => {
    if (!isSessionCard) {
      return ''
    }

    switch (sessionStatus) {
      case 'available':
        return 'border-green-200 bg-green-50'
      case 'booked':
        return 'border-yellow-200 bg-yellow-50'
      case 'cancelled':
        return 'border-red-200 bg-red-50'
      default:
        return ''
    }
  }

  return (
    <div
      className={`card-template bg-white border rounded-lg p-4 shadow-sm ${getStatusStyle()}`}
    >
      {content.image && (
        <img
          src={content.image}
          alt={content.title}
          className="w-full h-32 object-cover rounded-lg mb-3"
        />
      )}

      <h3 className="font-semibold text-gray-900 mb-2 text-sm leading-tight">
        {content.title}
      </h3>

      {content.subtitle && (
        <div className="text-xs text-gray-600 mb-3 whitespace-pre-line leading-relaxed">
          {content.subtitle}
        </div>
      )}

      {content.actions && content.actions.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3">
          {content.actions.map((action, index) => (
            <ActionButton
              key={index}
              action={action}
              size="sm"
              onClick={() => onAction?.(action)}
            />
          ))}
        </div>
      )}

      {/* Session-specific status indicator */}
      {isSessionCard && (
        <div className="mt-2 pt-2 border-t border-gray-100">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">
              Session ID: {content.data.session_id}
            </span>
            <div
              className={`px-2 py-1 rounded-full text-xs font-medium ${
                sessionStatus === 'available'
                  ? 'bg-green-100 text-green-700'
                  : sessionStatus === 'booked'
                  ? 'bg-yellow-100 text-yellow-700'
                  : 'bg-red-100 text-red-700'
              }`}
            >
              {sessionStatus === 'available'
                ? 'Available'
                : sessionStatus === 'booked'
                ? 'Booked'
                : 'Cancelled'}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// List Template Component
const ListTemplate: React.FC<{
  content: ListTemplate
  onAction?: (action: ResponseAction) => void
}> = ({ content, onAction }) => {
  return (
    <div className="list-template">
      {content.header && (
        <h3 className="font-semibold text-gray-900 mb-3">{content.header}</h3>
      )}
      <div className="space-y-2">
        {content.items.map((item, index) => (
          <div
            key={index}
            className="flex items-start space-x-3 p-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
          >
            {item.image && (
              <img
                src={item.image}
                alt={item.title}
                className="w-12 h-12 object-cover rounded-lg flex-shrink-0"
              />
            )}
            <div className="flex-1 min-w-0">
              <h4 className="font-medium text-gray-900 text-sm">
                {item.title}
              </h4>
              {item.subtitle && (
                <p className="text-xs text-gray-600 mt-1">{item.subtitle}</p>
              )}
              {item.actions && item.actions.length > 0 && (
                <div className="flex gap-1 mt-2">
                  {item.actions.map((action, actionIndex) => (
                    <ActionButton
                      key={actionIndex}
                      action={action}
                      size="sm"
                      onClick={() => onAction?.(action)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      {content.footer && (
        <p className="text-xs text-gray-500 mt-3">{content.footer}</p>
      )}
    </div>
  )
}

// Carousel Template Component
const CarouselTemplate: React.FC<{
  content: AppointmentCardTemplate[]
  onAction?: (action: ResponseAction) => void
}> = ({ content, onAction }) => {
  // Check if this is a session carousel for special styling
  const isSessionCarousel = content.length > 0 && content[0].data?.session_id

  return (
    <div className="carousel-template">
      <div
        className={`flex gap-3 overflow-x-auto pb-3 ${
          isSessionCarousel ? 'snap-x snap-mandatory' : ''
        }`}
      >
        {content.map((card, index) => (
          <div
            key={index}
            className={`flex-shrink-0 ${
              isSessionCarousel ? 'w-72 snap-center' : 'w-64'
            }`}
          >
            <AppointmentCardTemplate content={card} onAction={onAction} />
          </div>
        ))}
      </div>

      {/* Scrolling hint for session carousel */}
      {isSessionCarousel && content.length > 1 && (
        <div className="text-center mt-2">
          <p className="text-xs text-gray-500">
            ← Swipe to see more sessions ({content.length} slots) →
          </p>
        </div>
      )}
    </div>
  )
}

// Quick Replies Template Component
const QuickRepliesTemplate: React.FC<{
  content: QuickRepliesTemplate
  onQuickReply?: (payload: string) => void
}> = ({ content, onQuickReply }) => {
  // Validate content structure
  if (!content || typeof content !== 'object') {
    return (
      <div className="text-red-500 text-sm">
        Error: Invalid quick replies content
      </div>
    )
  }

  // Extract text and replies safely
  const text = typeof content.text === 'string' ? content.text : ''
  const replies = Array.isArray(content.replies) ? content.replies : []

  return (
    <div className="quick-replies-template">
      {text && <div className="text-sm mb-3">{text}</div>}
      <div className="flex flex-wrap gap-2">
        {replies.map((reply, index) => {
          // Validate each reply object
          if (
            !reply ||
            typeof reply !== 'object' ||
            !reply.title ||
            !reply.payload
          ) {
            console.error('Invalid reply object:', reply)
            return null
          }

          return (
            <button
              key={index}
              onClick={() => onQuickReply?.(reply.payload)}
              className="px-3 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 text-sm rounded-full border border-blue-200 transition-colors"
            >
              {reply.image && (
                <img
                  src={reply.image}
                  alt=""
                  className="w-4 h-4 inline-block mr-1"
                />
              )}
              {reply.title}
            </button>
          )
        })}
      </div>
    </div>
  )
}

// Form Template Component
const FormTemplate: React.FC<{
  content: FormTemplate
  onSubmit?: (data: Record<string, any>) => void
}> = ({ content, onSubmit }) => {
  const [formData, setFormData] = React.useState<Record<string, any>>({})
  const [errors, setErrors] = React.useState<Record<string, string>>({})

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    // Validate required fields
    const newErrors: Record<string, string> = {}
    content.fields.forEach(field => {
      if (field.required && !formData[field.name]) {
        newErrors[field.name] = `${field.label} is required`
      }

      // Validate regex patterns
      if (field.validation && formData[field.name]) {
        const regex = new RegExp(field.validation)
        if (!regex.test(formData[field.name])) {
          newErrors[field.name] = `${field.label} is not in the correct format`
        }
      }
    })

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    onSubmit?.(formData)
  }

  const handleFieldChange = (fieldName: string, value: any) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }))
    if (errors[fieldName]) {
      setErrors(prev => ({ ...prev, [fieldName]: '' }))
    }
  }

  return (
    <div className="form-template bg-gray-50 p-4 rounded-lg">
      <h3 className="font-semibold text-gray-900 mb-3">{content.title}</h3>
      <form onSubmit={handleSubmit} className="space-y-3">
        {content.fields.map((field, index) => (
          <div key={index}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {field.label}
              {field.required && <span className="text-red-500">*</span>}
            </label>

            {field.type === 'select' ? (
              <select
                value={formData[field.name] || ''}
                onChange={e => handleFieldChange(field.name, e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">{field.placeholder || 'Select...'}</option>
                {field.options?.map((option, optionIndex) => (
                  <option key={optionIndex} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            ) : field.type === 'textarea' ? (
              <textarea
                value={formData[field.name] || ''}
                onChange={e => handleFieldChange(field.name, e.target.value)}
                placeholder={field.placeholder}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
            ) : (
              <input
                type={field.type}
                value={formData[field.name] || ''}
                onChange={e => handleFieldChange(field.name, e.target.value)}
                placeholder={field.placeholder}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            )}

            {errors[field.name] && (
              <p className="text-red-500 text-xs mt-1">{errors[field.name]}</p>
            )}
          </div>
        ))}

        <div className="flex gap-2 pt-2">
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors"
          >
            {content.submitText || 'Submit'}
          </button>
          <button
            type="button"
            className="px-4 py-2 bg-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-400 transition-colors"
          >
            {content.cancelText || 'Cancel'}
          </button>
        </div>
      </form>
    </div>
  )
}

// Custom Template Component (for special cases)
const CustomTemplate: React.FC<{
  content: any
  onAction?: (action: ResponseAction) => void
}> = ({ content }) => {
  // This can be extended for specific custom templates
  return (
    <div className="custom-template">
      <pre className="whitespace-pre-wrap text-sm">{content}</pre>
    </div>
  )
}

// Reusable Action Button Component
const ActionButton: React.FC<{
  action: ResponseAction
  size?: 'sm' | 'md'
  onClick?: () => void
}> = ({ action, size = 'md', onClick }) => {
  const baseClasses =
    'inline-flex items-center justify-center font-medium rounded-md transition-colors'
  const sizeClasses = size === 'sm' ? 'px-2 py-1 text-xs' : 'px-3 py-2 text-sm'

  const styleClasses = {
    primary: 'bg-blue-600 hover:bg-blue-700 text-white',
    secondary: 'bg-gray-200 hover:bg-gray-300 text-gray-700',
    success: 'bg-green-600 hover:bg-green-700 text-white',
    warning: 'bg-yellow-500 hover:bg-yellow-600 text-white',
    danger: 'bg-red-600 hover:bg-red-700 text-white',
  }[action.style || 'primary']

  const handleClick = () => {
    if (onClick) {
      onClick?.()
    } else {
      if (action.type === 'link') {
        window.open(action.value, '_blank')
      } else if (action.type === 'call') {
        window.location.href = `tel:${action.value}`
      } else if (action.type === 'email') {
        window.location.href = `mailto:${action.value}`
      }
    }
  }

  return (
    <button
      onClick={handleClick}
      className={`${baseClasses} ${sizeClasses} ${styleClasses}`}
    >
      {action.label}
    </button>
  )
}

export default ResponseRenderer
